import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonModal, IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import type { ImagePlaceholder } from '../../types/listing';
import { useImagePlaceholders } from '../../hooks/useListings';
import { useAuthStore } from '../../store/useAuthStore';
import { searchImages } from '../../utils/imageSearch';

interface ImageLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (image: ImagePlaceholder) => void;
  currentImageUrl?: string | null;
}

export const ImageLibraryModal: React.FC<ImageLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentImageUrl,
}) => {
  const { t } = useTranslation('publish');
  // The catalog endpoint requires auth OR a `listing_generate` service key.
  // Authed users fetch via their session; logged-out users use the web key so
  // they can still browse the preset images while building a dish. Only fetch
  // when the modal is open.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const serviceKey = isAuthenticated
    ? undefined
    : (import.meta.env.VITE_GENERATE_SERVICE_KEY as string | undefined);
  const { data: placeholders, isLoading } = useImagePlaceholders(
    isOpen && (isAuthenticated || !!serviceKey),
    serviceKey,
  );
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Reset filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedCategory(null);
    }
  }, [isOpen]);

  // Extract unique categories from data
  const categories = useMemo(() => {
    if (!placeholders) return [];
    const cats = new Set<string>();
    for (const p of placeholders) {
      for (const c of p.categories ?? []) cats.add(c);
    }
    return Array.from(cats).sort();
  }, [placeholders]);

  // Filter by chip, then rank by search relevance
  const filtered = useMemo(() => {
    if (!placeholders) return [];
    return searchImages(placeholders, search, selectedCategory);
  }, [placeholders, search, selectedCategory]);

  const handleSelect = (img: ImagePlaceholder) => {
    onSelect(img);
    onClose();
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.92}
      breakpoints={[0, 0.92]}
      // Ionic's built-in handle defaults to `true` for breakpoint sheets; this
      // component already renders its own styled handle below, so suppress
      // Ionic's to avoid two stacked bars at the top.
      handle={false}
    >
      <IonContent>
        <div className="flex h-full flex-col">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="h-1 w-10 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-2 pb-3 shrink-0">
            <h2 className="text-lg font-semibold">{t('imageLibrary.title')}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-brand-primary"
            >
              {t('common:actions.cancel')}
            </button>
          </div>

          {/* Search */}
          <div className="px-4 pb-2 shrink-0">
            <div className="relative">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
              <input
                type="text"
                placeholder={t('imageLibrary.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>

          {/* Category chips */}
          {categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto px-4 pb-3 shrink-0 scrollbar-hide">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-brand-primary text-white'
                    : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                }`}
              >
                {t('imageLibrary.allCategory')}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() =>
                    setSelectedCategory(selectedCategory === cat ? null : cat)
                  }
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="border-b border-border shrink-0" />

          {/* Image grid */}
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-16">
            {isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <IonSpinner name="crescent" className="h-6 w-6" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="pt-8 text-center text-sm text-gray-500">
                {search || selectedCategory
                  ? t('imageLibrary.emptySearch')
                  : t('imageLibrary.empty')}
              </p>
            ) : (
              // Column min-width is in `rem`, so it scales with the root
              // font-size (the app's text-scale mechanism). As text grows the
              // cells need more room, fewer fit per row, and the grid drops to
              // a single column at large text — keeping the images big enough
              // for the (now larger) overlay labels.
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))' }}
              >
                {filtered.map((img) => {
                  const isSelected = currentImageUrl === img.imageUrl;
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => handleSelect(img)}
                      className={`group relative overflow-hidden rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-brand-primary ring-2 ring-brand-primary/20'
                          : 'border-transparent active:scale-[0.97]'
                      }`}
                    >
                      <div
                        className="aspect-[4/3] w-full"
                        style={{ backgroundColor: 'var(--ion-color-light)' }}
                      >
                        <img
                          src={img.thumbnailUrl ?? img.imageUrl}
                          alt={img.title}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      {/* Selected check */}
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white shadow">
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}

                      {/* Title overlay */}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pt-4 pb-1.5">
                        <p className="truncate text-left text-sm font-medium text-white">
                          {img.title}
                        </p>
                        {/* The tile has room for one line, so show the
                            chip in effect, else the image's primary
                            (first-listed) category. */}
                        {(selectedCategory ?? img.categories?.[0]) && (
                          <p className="truncate text-left text-xs text-white/70">
                            {selectedCategory ?? img.categories?.[0]}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};
